/* ==========================================================================
   SweetRose — motion & interaction layer
   Guardrails: every effect respects prefers-reduced-motion.
   ========================================================================== */
(() => {
  'use strict';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.documentElement.classList.add('sr-js');

  /* ---------- 1. Scroll reveal (staggered, one observer) ---------------- */
  const revealEls = document.querySelectorAll('[data-reveal], [data-clip]');
  if (revealEls.length) {
    if (reduce) {
      revealEls.forEach(el => el.classList.add('is-in'));
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
      revealEls.forEach(el => io.observe(el));
    }
  }

  // 兜底：观察器漏触发 / 元素异常时，强制显示所有待进场元素
  setTimeout(() => {
    document.querySelectorAll('[data-reveal]:not(.is-in), [data-clip]:not(.is-in)')
      .forEach(el => el.classList.add('is-in'));
  }, 2600);

  /* ---------- 2. Header state on scroll -------------------------------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    let last = 0, ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-stuck', y > 12);
      last = y; ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
    }, { passive: true });
    onScroll();
  }

  /* ---------- 3. Parallax (rAF-throttled, transform only) --------------- */
  const parallaxEls = [...document.querySelectorAll('[data-parallax]')];
  if (parallaxEls.length && !reduce) {
    let queued = false;
    const run = () => {
      const vh = window.innerHeight;
      parallaxEls.forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const speed = parseFloat(el.dataset.parallax) || 0.12;
        const progress = (r.top + r.height / 2 - vh / 2) / vh;   // -1..1
        el.style.transform = `translate3d(0, ${(-progress * speed * 100).toFixed(2)}px, 0) scale(1.06)`;
      });
      queued = false;
    };
    const onScrollP = () => { if (!queued) { queued = true; requestAnimationFrame(run); } };
    window.addEventListener('scroll', onScrollP, { passive: true });
    window.addEventListener('resize', onScrollP);
    run();
  }

  /* ---------- 4. Magnetic buttons -------------------------------------- */
  if (!reduce && window.matchMedia('(hover: hover)').matches) {
    document.querySelectorAll('[data-magnetic]').forEach(btn => {
      const strength = parseFloat(btn.dataset.magnetic) || 0.28;
      const label = btn.querySelector('span') || btn;
      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) * strength;
        const y = (e.clientY - r.top - r.height / 2) * strength;
        btn.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        label.style.transform = `translate3d(${x * 0.28}px, ${y * 0.28}px, 0)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
        label.style.transform = '';
      });
    });
  }

  /* ---------- 5. Mobile drawer ----------------------------------------- */
  const drawer = document.querySelector('[data-drawer]');
  const openers = document.querySelectorAll('[data-drawer-open]');
  const closers = document.querySelectorAll('[data-drawer-close]');
  const setDrawer = (open) => {
    if (!drawer) return;
    drawer.dataset.open = String(open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.body.style.overflow = open ? 'hidden' : '';
  };
  openers.forEach(b => b.addEventListener('click', () => setDrawer(true)));
  closers.forEach(b => b.addEventListener('click', () => setDrawer(false)));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setDrawer(false); });

  /* ---------- 6. Scroll lock helper for cart --------------------------- */
  const lockScroll = (on) => { document.body.style.overflow = on ? 'hidden' : ''; };

  /* ---------- 7. AJAX add-to-cart with morphing feedback --------------- */
  document.querySelectorAll('form[data-ajax-cart]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[type=submit]');
      const original = btn ? btn.innerHTML : '';
      if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.innerHTML = '<span>Adding…</span>'; }
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: new FormData(form),
        });
        if (!res.ok) throw new Error('add failed');
        await refreshCart();
        if (btn) {
          btn.innerHTML = '<span>Added ✦</span>';
          setTimeout(() => { btn.innerHTML = original; btn.removeAttribute('aria-disabled'); }, 1600);
        }
        openCart();
      } catch (err) {
        if (btn) { btn.innerHTML = '<span>Try again</span>'; btn.removeAttribute('aria-disabled'); }
        setTimeout(() => { if (btn) btn.innerHTML = original; }, 1800);
      }
    });
  });

  /* ---------- 8. Cart drawer (slides from right) ----------------------- */
  const cartPanel = document.querySelector('[data-cart-panel]');
  const cartScrim = document.querySelector('[data-cart-scrim]');
  function openCart() {
    if (!cartPanel) { window.location.href = '/cart'; return; }
    cartPanel.dataset.open = 'true';
    cartPanel.setAttribute('aria-hidden', 'false');
    if (cartScrim) cartScrim.dataset.open = 'true';
    lockScroll(true);
  }
  function closeCart() {
    if (!cartPanel) return;
    cartPanel.dataset.open = 'false';
    cartPanel.setAttribute('aria-hidden', 'true');
    if (cartScrim) cartScrim.dataset.open = 'false';
    lockScroll(false);
  }
  document.querySelectorAll('[data-cart-open]').forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); openCart(); }));
  document.querySelectorAll('[data-cart-close]').forEach(b => b.addEventListener('click', closeCart));
  if (cartScrim) cartScrim.addEventListener('click', closeCart);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeCart(); });

  async function refreshCart() {
    const res = await fetch('/?sections=cart-drawer,cart-icon-bubble');
    const data = await res.json();
    if (data['cart-icon-bubble']) {
      const holder = document.querySelector('[data-cart-count-holder]');
      if (holder) holder.innerHTML = data['cart-icon-bubble'];
    }
    if (data['cart-drawer']) {
      const body = document.querySelector('[data-cart-body]');
      if (body) {
        const tmp = document.createElement('div');
        tmp.innerHTML = data['cart-drawer'];
        const fresh = tmp.querySelector('[data-cart-body]');
        if (fresh) body.innerHTML = fresh.innerHTML;
      }
    }
  }

  /* ---------- 9. PDP gallery ------------------------------------------- */
  const pdpHero = document.querySelector('[data-pdp-hero] img');
  document.querySelectorAll('[data-pdp-thumb]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.full || thumb.querySelector('img')?.src;
      if (pdpHero && src) {
        pdpHero.style.opacity = '0';
        setTimeout(() => { pdpHero.src = src; pdpHero.style.opacity = '1'; }, 140);
      }
      document.querySelectorAll('[data-pdp-thumb]').forEach(t => t.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
    });
  });

  /* ---------- 9b. Mobile sticky buy bar -------------------------------- */
  const sticky = document.querySelector('[data-stickybuy]');
  const mainForm = document.querySelector('.sr-pdp__form');
  if (sticky && mainForm && 'IntersectionObserver' in window) {
    const sio = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        const shown = !e.isIntersecting && e.boundingClientRect.top < 0;
        sticky.classList.toggle('is-shown', shown);
        sticky.setAttribute('aria-hidden', String(!shown));
      });
    }, { threshold: 0 });
    sio.observe(mainForm);
  }

  /* ---------- 10. Quantity steppers ------------------------------------ */
  document.querySelectorAll('[data-qty]').forEach(wrap => {
    const input = wrap.querySelector('input');
    wrap.querySelectorAll('button').forEach(btn => {
      btn.addEventListener('click', () => {
        const step = btn.dataset.step === 'up' ? 1 : -1;
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + step);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  /* ---------- 11. Cart line updates (debounced) ------------------------ */
  let cartTimer;
  document.querySelectorAll('[data-cart-form] [name="updates[]"]').forEach(input => {
    input.addEventListener('change', () => {
      clearTimeout(cartTimer);
      cartTimer = setTimeout(() => {
        document.querySelector('[data-cart-form]')?.submit();
      }, 700);
    });
  });

  /* ---------- 12. Free-shipping progress ------------------------------- */
  document.querySelectorAll('[data-shipbar]').forEach(bar => {
    const threshold = parseFloat(bar.dataset.shipbar) || 0;
    const total = parseFloat(bar.dataset.total) || 0;
    const fill = bar.querySelector('.sr-shipbar__fill');
    const note = bar.querySelector('[data-shipbar-note]');
    if (!fill || !threshold) return;
    const pct = Math.min(100, (total / threshold) * 100);
    requestAnimationFrame(() => { fill.style.width = pct + '%'; });
    if (note) {
      note.textContent = total >= threshold
        ? 'You have unlocked complimentary worldwide shipping ✦'
        : `Spend ${(threshold - total).toFixed(2)} more for complimentary shipping`;
    }
  });
})();
